import { Injectable, inject } from '@angular/core';
import {
    IAccessRightCoreFilter,
    IAccessRightSelectionFiltersCollection,
    SelectionFilterModel,
} from '@fi-shared/models';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { IAccessRightsCoreFiltersErrors } from '@fi-shared/fi-shared-services/fi-access-right-helper.service';
import { fiValidationHasErrors } from '@fi-shared/utils/fi-validation-has-errors';
import { AccessRightService, FiSnackBarService } from '@fi-shared/fi-shared-services';
import { AppTextService } from '@fi-shared/app-text';
import { removeItem } from '@fi-shared/utils/remove-item';
import { ModuleType } from '@fi-shared/types';
import { FiItemSharingSettingsDialogService } from '@fi-app/shared/access-right-filters/fi-item-sharing-settings-dialog/fi-item-sharing-settings.service';

@Injectable()
export class SharingSettingsHelperService {
    private readonly accessRightService = inject(AccessRightService);
    private readonly fiSnackBarService = inject(FiSnackBarService);
    private readonly appTextService = inject(AppTextService);
    private readonly fiItemSharingSettingsDialogService = inject(FiItemSharingSettingsDialogService);

    /**
     * Checks if sharing settings have changed compared to the selected filter collection
     */
    isSharingSettingsChanged(
        accessRightCoreFilter: IAccessRightCoreFilter,
        selectedFiltersCollection: IAccessRightSelectionFiltersCollection | undefined,
        doRoleIdsCheck?: boolean,
    ): boolean {
        if (!isDefined(selectedFiltersCollection)) {
            return Object.keys(accessRightCoreFilter).some((key) => {
                if (accessRightCoreFilter[key]?.length) return true;
            });
        }

        return !this.fiItemSharingSettingsDialogService.isAccessRightFilterEqualToFilterModel(
            accessRightCoreFilter,
            selectedFiltersCollection.SelectionFilterSettingCollection.AccessRightCoreFilter,
            doRoleIdsCheck,
        );
    }

    /**
     * Checks if the new quick selection button should be disabled
     */
    isNewQuickSelectionDisabled(
        validationErrors: IAccessRightsCoreFiltersErrors,
        accessRightCoreFilter: IAccessRightCoreFilter,
        selectedFiltersCollection: IAccessRightSelectionFiltersCollection | undefined,
        doRoleIdsCheck?: boolean,
    ): boolean {
        return (
            fiValidationHasErrors(validationErrors) ||
            !this.isSharingSettingsChanged(accessRightCoreFilter, selectedFiltersCollection, doRoleIdsCheck)
        );
    }

    /**
     * Checks if update quick selection button should be visible
     */
    isUpdateQuickSelectionCollectionButtonVisible(
        isSaveAsNewQuickSelection: boolean,
        selectedFiltersCollection: IAccessRightSelectionFiltersCollection | undefined,
        accessRightCoreFilter: IAccessRightCoreFilter,
        doRoleIdsCheck: boolean,
    ): boolean {
        return (
            !isSaveAsNewQuickSelection &&
            isDefined(selectedFiltersCollection) &&
            !this.fiItemSharingSettingsDialogService.isAccessRightFilterEqualToFilterModel(
                accessRightCoreFilter,
                selectedFiltersCollection.SelectionFilterSettingCollection.AccessRightCoreFilter,
                doRoleIdsCheck,
            )
        );
    }

    /**
     * Updates a quick selection filter
     */
    updateQuickSelection(
        selectedFiltersCollection: IAccessRightSelectionFiltersCollection,
        accessRightCoreFilter: IAccessRightCoreFilter,
        moduleType: ModuleType,
    ): Promise<void> {
        const selectionFilterModel = this.getSelectionFilterModelToUpdate(
            selectedFiltersCollection,
            accessRightCoreFilter,
        );

        return this.accessRightService.SaveSelectionFilter(selectionFilterModel, moduleType).then(() => {
            selectedFiltersCollection.SelectionFilterSettingCollection.AccessRightCoreFilter =
                cloneDeep(accessRightCoreFilter);

            this.showSnackbar(
                this.appTextService.get('common.item_updated', { '{0}': selectedFiltersCollection.Name }),
            );
        });
    }

    /**
     * Deletes a quick selection filter
     */
    deleteQuickSelection(
        filterCollection: IAccessRightSelectionFiltersCollection,
        savedQuickSelectionFilters: IAccessRightSelectionFiltersCollection[],
        moduleType: ModuleType,
    ): Promise<IAccessRightSelectionFiltersCollection[]> {
        return this.accessRightService.DeleteSelectionFilter(filterCollection.Id, moduleType).then(() => {
            removeItem(savedQuickSelectionFilters, filterCollection);
            this.showSnackbar(
                this.appTextService.get('common.item_has_been_deleted', { '{0}': filterCollection.Name }),
            );
            return [...savedQuickSelectionFilters];
        });
    }

    /**
     * Initializes a new selection filter model
     */
    initNewSelectionFilterModel(
        accessRightCoreFilter: IAccessRightCoreFilter,
        accessRightUserFilter?: any,
    ): SelectionFilterModel {
        const newSelectionFilterModel = new SelectionFilterModel();
        newSelectionFilterModel.IsActivated = true;
        newSelectionFilterModel.InitializeSelectionFilterSettingCollection({
            AccessRightCoreFilter: accessRightCoreFilter,
            AccessRightUserFilter: accessRightUserFilter,
        });
        return newSelectionFilterModel;
    }

    /**
     * Creates a selection filter model for updating
     */
    private getSelectionFilterModelToUpdate(
        selectedFiltersCollection: IAccessRightSelectionFiltersCollection,
        accessRightCoreFilter: IAccessRightCoreFilter,
    ): SelectionFilterModel {
        selectedFiltersCollection.SelectionFilterSettingCollection.AccessRightCoreFilter =
            cloneDeep(accessRightCoreFilter);
        const selectionFilter = new SelectionFilterModel();
        selectionFilter.Initialize(selectedFiltersCollection);
        selectionFilter.IsActivated = true;
        selectionFilter.IsDefault = selectedFiltersCollection.IsDefault;
        return selectionFilter;
    }

    /**
     * Shows a snackbar notification
     */
    private showSnackbar(textMessage: string): void {
        this.fiSnackBarService.showSnackbar(
            {
                textMessage: textMessage,
                buttonIconClass: 'fas fa-xmark',
                isSnackbarBorderShown: true,
            },
            false,
            'fi-snackbar-standard',
        );
    }
}
